import React, { useState } from 'react';
import {
    Box,
    Typography,
    IconButton,
    Menu,
    MenuItem,
    CircularProgress
} from '@mui/material';
import { MoreVertIcon, DeleteOutlineIcon } from 'icons/materialIcons';
import { useTeam } from 'team/contexts/teamContext';
import { deleteTeamApiKey } from 'team/apis/teamApi';
import TeamNotificationSnackbar from 'team/shared/components/notification/TeamNotificationSnackbar';

const TeamApiKeysList = ({ apiKeys, loading, onRefresh }) => {
    const { currentTeamId } = useTeam();
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedKey, setSelectedKey] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const handleMenuOpen = (event, key) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
        setSelectedKey(key);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleDeleteKey = async () => {
        if (!selectedKey) return;
        setDeleteLoading(true);
        try {
            const response = await deleteTeamApiKey(currentTeamId, selectedKey._id);
            if (response && response.success) {
                setSnackbar({
                    open: true,
                    message: 'API Key deleted successfully!',
                    severity: 'success'
                });
                onRefresh();
            }
        } catch (error) {
            console.error('Delete API Key Error:', error);
            setSnackbar({
                open: true,
                message: error.message || 'Failed to delete API key',
                severity: 'error'
            });
        } finally {
            setDeleteLoading(false);
            handleMenuClose();
            setSelectedKey(null);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 8 }}>
                <CircularProgress size={32} />
                <Typography sx={{ ml: 2, color: '#666' }}>Loading API keys...</Typography>
            </Box>
        );
    }

    if (!apiKeys || apiKeys.length === 0) {
        return (
            <Box sx={{
                border: '0.0625rem dashed #e0e0e0',
                borderRadius: '0.75rem',
                backgroundColor: '#fafafa',
                padding: 6,
                textAlign: 'center'
            }}>
                <Typography variant="body1" sx={{ color: '#666', mb: 1, fontWeight: 600 }}>
                    API Keys
                </Typography>
                <Typography variant="body2" sx={{ color: '#999' }}>
                    You have not created any API Keys. Create a new API key to get started.
                </Typography>
            </Box>
        );
    }

    return (
        <>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {/* Header for the list style columns */}
                <Box sx={{ display: 'flex', px: 2, mb: 1, alignItems: 'center' }}>
                    <Typography variant="caption" sx={{ flex: 1, color: '#828282', fontWeight: 600 }}>
                        LABEL NAME
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#828282', fontWeight: 600, mr: 7 }}>
                        LAST USED
                    </Typography>
                    <Box sx={{ width: 44 }} /> {/* Space for dots */}
                </Box>

                {apiKeys.map((key) => (
                    <Box
                        key={key._id}
                        sx={{
                            border: '1px solid #e0e0e0',
                            borderRadius: '0.75rem',
                            padding: 2,
                            display: 'flex',
                            alignItems: 'center',
                            backgroundColor: '#fff',
                            transition: 'all 0.2s',
                            '&:hover': {
                                boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                                borderColor: '#d0d0d0'
                            }
                        }}
                    >
                        <Box sx={{ flex: 1 }}>
                            <Typography sx={{ fontWeight: 600, fontSize: '0.9rem', color: '#333' }}>
                                {key.label}
                            </Typography>
                            <Typography sx={{ fontSize: '0.75rem', color: '#828282', fontFamily: 'monospace', mt: 0.5 }}>
                                {key.keyPrefix}
                            </Typography>
                        </Box>

                        <Box sx={{ mr: 7, minWidth: '80px', textAlign: 'right' }}>
                            <Typography sx={{ fontSize: '0.85rem', color: '#4f4f4f' }}>
                                {key.lastUsedAt ? new Date(key.lastUsedAt).toLocaleDateString() : ''}
                            </Typography>
                        </Box>

                        <IconButton onClick={(e) => handleMenuOpen(e, key)} sx={{ color: '#828282', p: 1 }}>
                            <MoreVertIcon sx={{ fontSize: 20 }} />
                        </IconButton>
                    </Box>
                ))}
            </Box>

            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                PaperProps={{
                    sx: {
                        boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
                        borderRadius: '0.5rem',
                        minWidth: '200px'
                    }
                }}
            >
                <MenuItem
                    onClick={handleDeleteKey}
                    disabled={deleteLoading}
                    sx={{ color: '#d32f2f' }}
                >
                    <DeleteOutlineIcon fontSize="small" sx={{ mr: 1 }} />
                    Delete API Key
                </MenuItem>
            </Menu>



            <TeamNotificationSnackbar
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                handleClose={() => setSnackbar({ ...snackbar, open: false })}
            />
        </>
    );
};

export default TeamApiKeysList;
